/**
 * Meta Pixel (Facebook Pixel) utility functions
 * This file provides functions to track events with the Meta Pixel
 */

// Get the pixel ID from environment variables
const PIXEL_ID = process.env.REACT_APP_PIXEL_ID || process.env.PIXEL_ID;

/**
 * Initialize the Meta Pixel
 * This should be called once when the app loads
 */
export const initializePixel = () => {
  if (!PIXEL_ID) {
    console.warn("Meta Pixel ID is not defined in environment variables");
    return;
  }

  // Check if fbq is already defined (pixel code already loaded)
  if (window.fbq) {
    console.log("📊 Meta Pixel already initialized");
    return;
  }

  try {
    // Load the pixel code
    // eslint-disable-next-line no-unused-expressions
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );

    // Initialize with the Pixel ID
    window.fbq("init", PIXEL_ID);

    // Track the initial page view
    window.fbq("track", "PageView");

    console.log("📊 Meta Pixel initialized with ID:", PIXEL_ID);

    // Verify that fbq is working correctly
    if (typeof window.fbq === "function") {
      console.log("✅ Meta Pixel function (fbq) is available and ready to use");
    } else {
      console.warn(
        "⚠️ Meta Pixel function (fbq) is not available after initialization"
      );
    }
  } catch (error) {
    console.error("❌ Error initializing Meta Pixel:", error);
  }
};

/**
 * Track a page view event
 */
export const trackPageView = () => {
  if (window.fbq) {
    window.fbq("track", "PageView");

    // Log cho debugging
    if (process.env.NODE_ENV === "development") {
      console.log("📊 Meta Pixel: PageView event tracked");
    }
  } else {
    console.warn(
      "Meta Pixel: Không thể track PageView vì fbq chưa được khởi tạo"
    );
  }
};

/**
 * Track a custom event
 * @param {string} eventName - The name of the event to track
 * @param {Object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (window.fbq) {
    // Sử dụng 'trackCustom' thay vì 'track' cho các sự kiện tùy chỉnh
    window.fbq("trackCustom", eventName, params);

    // Log cho debugging
    if (process.env.NODE_ENV === "development") {
      console.log(
        `📊 Meta Pixel: Custom event '${eventName}' tracked with params:`,
        params
      );
    }
  } else {
    console.warn(
      `Meta Pixel: Không thể track sự kiện '${eventName}' vì fbq chưa được khởi tạo`
    );
  }
};

/**
 * Track when a user completes registration
 * @param {Object} params - Additional parameters
 */
export const trackCompleteRegistration = (params = {}) => {
  if (window.fbq) {
    window.fbq("track", "CompleteRegistration", params);

    // Log cho debugging
    if (process.env.NODE_ENV === "development") {
      console.log(
        "📊 Meta Pixel: CompleteRegistration event tracked with params:",
        params
      );
    }
  } else {
    console.warn(
      "Meta Pixel: Không thể track CompleteRegistration vì fbq chưa được khởi tạo"
    );
  }
};

/**
 * Track when a user initiates checkout
 * @param {Object} params - Additional parameters including content_ids, value, currency
 */
export const trackInitiateCheckout = (params = {}) => {
  if (window.fbq) {
    window.fbq("track", "InitiateCheckout", params);
  }
};

/**
 * Track when a purchase is completed
 * @param {Object} params - Parameters including value, currency, content_ids
 */
export const trackPurchase = (params = {}) => {
  if (window.fbq) {
    window.fbq("track", "Purchase", params);
  }
};

/**
 * Track when a user adds a product to cart
 * @param {Object} params - Parameters including content_ids, content_name, value
 */
export const trackAddToCart = (params = {}) => {
  if (window.fbq) {
    window.fbq("track", "AddToCart", params);
  }
};

/**
 * Track when a user views content
 * @param {Object} params - Parameters including content_ids, content_type
 */
export const trackViewContent = (params = {}) => {
  if (window.fbq) {
    window.fbq("track", "ViewContent", params);
  }
};

export default {
  initializePixel,
  trackPageView,
  trackEvent,
  trackCompleteRegistration,
  trackInitiateCheckout,
  trackPurchase,
  trackAddToCart,
  trackViewContent,
};
